import React, { useMemo, useState } from 'react';
import { Table, Typography, Input } from 'antd';
import { SearchOutlined, InboxOutlined, DollarCircleOutlined } from '@ant-design/icons';
import { ReceiptDetailInline } from './ReceiptDetailInline';
import { formatCurrency, formatDate } from '../../utils/formatters';

export type TransactionType = 'receipt' | 'payment';

export interface TransactionItem {
  id: string;
  type: TransactionType;
  code: string;
  date: string;
  description?: string;
  amount: number;
  status?: string;
  runningBalance?: number;
  receiptId?: number;
  relatedVoucherId?: number;
}

interface TransactionHistoryProps {
  token: string;
  password: string;
  transactions: TransactionItem[];
}

const typeMeta: Record<TransactionType, { label: string; color: string; icon: React.ReactNode }> = {
  receipt: {
    label: 'Nhập hàng',
    color: '#0ea5e9',
    icon: <InboxOutlined />,
  },
  payment: {
    label: 'Thanh toán',
    color: '#16a34a',
    icon: <DollarCircleOutlined />,
  },
};

const TransactionHistory: React.FC<TransactionHistoryProps> = ({ token, password, transactions }) => {
  const [search, setSearch] = useState('');
  const filteredTransactions = useMemo(() => {
    if (!search) {
      return transactions;
    }
    const keyword = search.toLowerCase();
    return transactions.filter(
      (tx) =>
        tx.code?.toLowerCase().includes(keyword) ||
        tx.description?.toLowerCase().includes(keyword)
    );
  }, [transactions, search]);

  const columns = [
    {
      title: 'Loại giao dịch',
      key: 'type',
      render: (_: any, record: TransactionItem) => {
        const meta = typeMeta[record.type];
        return (
          <div className="portal-transaction-type">
            <span
              className="portal-transaction-type__icon"
              style={{ background: `${meta.color}20`, color: meta.color }}
            >
              {meta.icon}
            </span>
            <div>
              <Typography.Text strong>{meta.label}</Typography.Text>
              {record.type === 'receipt' && record.status && (
                <Typography.Text
                  type="secondary"
                  style={{ display: 'block', marginTop: 4, fontSize: 12 }}
                >
                  {record.status === 'completed' ? 'Hoàn thành' : 'Chờ xử lý'}
                </Typography.Text>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Mã chứng từ',
      dataIndex: 'code',
      key: 'code',
      render: (value: string) => value || '—',
    },
    {
      title: 'Ngày ghi nhận',
      dataIndex: 'date',
      key: 'date',
      render: (value: string) => (value ? formatDate(value) : '—'),
    },
    {
      title: 'Diễn giải',
      dataIndex: 'description',
      key: 'description',
      width: 240,
      render: (value: string) => value || '—',
    },
    {
      title: 'Giá trị',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      render: (value: number) => {
        const isPositive = value >= 0;
        return (
          <Typography.Text strong style={{ color: isPositive ? '#0f172a' : '#16a34a' }}>
            {isPositive ? '+' : ''}
            {formatCurrency(Math.abs(value))}
          </Typography.Text>
        );
      },
    },
    {
      title: 'Dư nợ lũy kế',
      dataIndex: 'runningBalance',
      key: 'runningBalance',
      align: 'right' as const,
      render: (value: number | undefined) => (
        <Typography.Text>{formatCurrency(value || 0)}</Typography.Text>
      ),
    },
  ];

  return (
    <>
      <div className="portal-transaction-toolbar">
        <Typography.Text type="secondary">
          {filteredTransactions.length} giao dịch gần nhất
        </Typography.Text>
        <Input
          placeholder="Tìm theo mã chứng từ..."
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }}
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredTransactions}
        rowKey="id"
        pagination={{
          pageSize: 10,
          size: 'small',
          showTotal: (total) => `Tổng cộng: ${total} giao dịch`,
        }}
        size="small"
        expandable={{
          expandRowByClick: true,
          columnWidth: 48,
          expandedRowRender: (record: TransactionItem) =>
            record.receiptId ? (
              <ReceiptDetailInline token={token} password={password} receiptId={record.receiptId} />
            ) : (
              <Typography.Text type="secondary">Không có chi tiết</Typography.Text>
            ),
          rowExpandable: (record) => !!record.receiptId,
        }}
      />
    </>
  );
};

export default TransactionHistory;

